/**
 * Location Bridge
 * 
 * Provides geolocation data.
 * Full support via Geolocation API.
 */

import { StreamingBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities, LocationData, LocationOptions } from '../types'

type DataCallback = (subscriptionId: string, data: any) => void

export class LocationBridge extends StreamingBridge {
  readonly bridgeName = 'Location'
  readonly availability: BridgeAvailability = 'full'
  readonly methods = ['getLocation', 'startListening', 'stopListening', 'setCallback']
  
  private watchIds: Map<string, number> = new Map()
  private sendData: DataCallback
  
  constructor(sendData: DataCallback) {
    super()
    this.sendData = sendData
  }
  
  async handleCall(method: string, args: any[]): Promise<any> {
    switch (method) {
      case 'getLocation':
        return JSON.stringify(await this.getLocation())
      case 'startListening':
        return this.startListeningInternal()
      case 'stopListening':
        return this.stopListeningInternal()
      case 'setCallback':
        // Callback name not used in web implementation - data sent via postMessage
        void args[0]
        return true
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private async getLocation(): Promise<LocationData> {
    if (!('geolocation' in navigator)) {
      throw this.createNotSupportedError('Geolocation')
    }
    
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(this.positionToLocationData(position))
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              reject(this.createPermissionDeniedError('location'))
              break
            case error.POSITION_UNAVAILABLE:
              reject({
                code: 'NOT_SUPPORTED',
                message: 'Location information is unavailable',
                action: 'NONE'
              })
              break
            case error.TIMEOUT:
              reject({
                code: 'TIMEOUT',
                message: 'Location request timed out',
                action: 'NONE'
              })
              break
            default:
              reject({
                code: 'INTERNAL_ERROR',
                message: error.message,
                action: 'NONE'
              })
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      )
    })
  }
  
  private startListeningInternal(): boolean {
    // This is called when SDK wants to start without subscription
    // The actual watching happens in startStreaming
    return true
  }
  
  private stopListeningInternal(): boolean {
    // Stop all watches
    for (const [subscriptionId, watchId] of this.watchIds) {
      navigator.geolocation.clearWatch(watchId)
      this.subscriptions.delete(subscriptionId)
    }
    this.watchIds.clear()
    return true
  }
  
  startStreaming(subscriptionId: string, options?: LocationOptions): void {
    if (!('geolocation' in navigator)) {
      return
    }
    
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const data = this.positionToLocationData(position)
        this.sendData(subscriptionId, data)
      },
      (error) => {
        this.sendData(subscriptionId, {
          error: error.message,
          code: error.code
        })
      },
      {
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        timeout: options?.timeout ?? 10000,
        maximumAge: options?.maximumAge ?? 0
      }
    )
    
    this.watchIds.set(subscriptionId, watchId)
    this.subscriptions.set(subscriptionId, true)
  }
  
  stopStreaming(subscriptionId: string): void {
    const watchId = this.watchIds.get(subscriptionId)
    
    if (watchId !== undefined) {
      navigator.geolocation.clearWatch(watchId)
      this.watchIds.delete(subscriptionId)
    }
    
    this.subscriptions.delete(subscriptionId)
  }
  
  private positionToLocationData(position: GeolocationPosition): LocationData {
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
      timestamp: position.timestamp
    }
  }
  
  cleanup(): void {
    this.stopListeningInternal()
    super.cleanup()
  }
  
  getCapabilities(): BridgeCapabilities {
    const hasGeolocation = 'geolocation' in navigator
    
    return {
      getLocation: { 
        available: hasGeolocation,
        requiresPermission: true
      },
      startListening: { 
        available: hasGeolocation,
        requiresPermission: true
      },
      stopListening: { available: hasGeolocation },
      setCallback: { available: true }
    }
  }
}

